import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, inject, NgModule } from '@angular/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent, AppComponentModule } from './app.component';

import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';

import { OverlayContainer } from '@angular/cdk/overlay';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { provideComponentStore } from '@ngrx/component-store';
import { FormlyModule } from '@ngx-formly/core';
import { SecurityConfigurationStore } from '@siza-staff-care/security-configuration-paint';
import { CustomErrorHandler } from '@siza-staff-care/shared/error-handler';
import { GenericDialogsModule } from '@siza-staff-care/shared/generic-dialogs';
import { Global } from '@siza-staff-care/shared/global-services';
import {
	DatesInterceptor,
	DEFAULT_TIMEOUT,
	Interceptor,
	JwtAuthInterceptor,
	MAX_RETRIES,
	RetryInterceptor,
	TimeoutInterceptor,
} from '@siza-staff-care/shared/interceptors';
import { WebAppShellModule } from '@siza-staff-care/web-app/shell';
import { HotkeyModule } from 'angular2-hotkeys';
import { NgEventBus } from 'ng-event-bus';
import { CdkOverlayContainer } from './cdk-overlay-container';
import { StartupCustomisationService } from './startup-customisation.service';

const DefaultTimeoutMs = 30_000;
const MaxRetries = 3;
const SnackbarDefaultDurationMs = 3_000;

function initApp(svc: StartupCustomisationService) {
	return () => svc.loadConfigurationData();
}

@NgModule({
	imports: [
		BrowserModule,
		HttpClientModule,
		AppComponentModule,
		HotkeyModule.forRoot({
			cheatSheetCloseEsc: true,
		}),
		WebAppShellModule,
		MatSnackBarModule,
		FormlyModule.forRoot({
			extras: {
				checkExpressionOn: 'modelChange', // New behaviour
				lazyRender: false, // Old behaviour - setting to true breaks (e.g. alert groups in staff records/details)
				resetFieldOnHide: false, // Don't reset fields on hide - setting to true makes models appear dirty, when they're not.
			},
		}),
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: environment.production,
			// Register the ServiceWorker as soon as the app is stable
			// or after 30 seconds (whichever comes first).
			registrationStrategy: 'registerWhenStable:30000',
		}),
		BrowserAnimationsModule,
		FontAwesomeModule,
		GenericDialogsModule,
	],
	providers: [
		NgEventBus,

		{
			provide: APP_INITIALIZER,
			useFactory: initApp,
			multi: true,
			deps: [StartupCustomisationService],
		},

		{ provide: HTTP_INTERCEPTORS, useClass: DatesInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: JwtAuthInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: RetryInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: Interceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },

		{ provide: DEFAULT_TIMEOUT, useValue: DefaultTimeoutMs },
		{ provide: MAX_RETRIES, useValue: MaxRetries },

		{ provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: SnackbarDefaultDurationMs } },
		{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill', subscriptSizing: 'dynamic' } },
		{
			provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
			useValue: { showDelay: 500, hideDelay: 0, touchendHideDelay: 1500 },
		},

		// Provide the SecurityConfigurationStore at the root level, so that it can be injected into the app's components and
		// also any overlay panels.
		provideComponentStore(SecurityConfigurationStore),

		{ provide: OverlayContainer, useClass: CdkOverlayContainer },
		{ provide: ErrorHandler, useClass: CustomErrorHandler },
	],
	bootstrap: [AppComponent],
})
export class AppModule {
	private global = inject(Global);
	constructor() {
		this.global.appVersion = environment.deployInfo.version;
	}
}
